import { useTranslation } from 'react-i18next';
import type { ExchangeUiState } from '@/lib/exchangeOffer';
import type { SaleOrderStatus, SaleOrder } from '@/types/sell';
import classes from './LotStatus.module.css';

/** Состояние биржевых статусов: загружены, грузятся или запрос не удался. */
export type ExchangeStatusesState = 'ready' | 'loading' | 'failed';

const STATUS_KEY: Partial<Record<SaleOrderStatus, string>> = {
  ACTIVE: 'app.sell.status.active',
  SUSPENDED: 'app.sell.status.suspended',
  MODERATED: 'app.sell.status.moderated',
  DRAFT: 'app.sell.status.draft',
  UNDER_BARGAIN: 'app.sell.status.underBargain',
  CLOSED: 'app.sell.status.closed',
  CANCELED: 'app.sell.status.canceled',
};

/** Ключ подписи статуса оффера; null — статус неизвестен интерфейсу (выводится как есть). */
export function saleStatusKey(status: SaleOrderStatus): string | null {
  return STATUS_KEY[status] ?? null;
}

const EXCHANGE_LABEL: Record<ExchangeUiState, string> = {
  ON_EXCHANGE: 'app.sell.exchange.onExchange',
  OFF_EXCHANGE: 'app.sell.exchange.offExchange',
  FAILED: 'app.sell.exchange.failed',
  MATCHED: 'app.sell.exchange.matched',
};

interface LotStatusProps {
  readonly offer: SaleOrder;
  /** Биржевое состояние лота; `null` — внебиржевой лот или состояние ещё неизвестно. */
  readonly state: ExchangeUiState | null;
  readonly exchangeState: ExchangeStatusesState;
}

// Один статус лота текстом — в таблице «Продать» и на странице предложения. Порядок важности: сделка, живая заявка
// на бирже (она торгуется, даже если предложение приостановлено, — так бывает, когда при потере аттестации биржа
// не сняла заявку), пауза, остальные состояния на бирже, статус платформы. Обычное состояние («Активен»,
// «Не на бирже») — спокойным серым, исключения выделены.
export default function LotStatus({ offer, state, exchangeState }: LotStatusProps) {
  const { t } = useTranslation();
  if (offer.status === 'UNDER_BARGAIN' || state === 'MATCHED') {
    return <span className={classes.good}>{t('app.sell.status.underBargain')}</span>;
  }
  if (state === 'ON_EXCHANGE') {
    return <span className={classes.good}>{t('app.sell.exchange.onExchange')}</span>;
  }
  if (offer.status === 'SUSPENDED') {
    return <span className={classes.strong}>{t('app.sell.status.suspended')}</span>;
  }
  if (offer.toExchange && offer.status === 'ACTIVE') {
    if (exchangeState === 'loading') {
      return <span className={classes.soft}>{t('app.sell.exchange.loading')}</span>;
    }
    if (exchangeState === 'failed' || state == null) {
      return <span className={classes.soft}>—</span>;
    }
    const tone = state === 'FAILED' ? classes.bad : classes.soft;
    return <span className={tone}>{t(EXCHANGE_LABEL[state])}</span>;
  }
  if (!offer.status) {
    return null;
  }
  const key = saleStatusKey(offer.status);
  return <span className={offer.status === 'ACTIVE' ? classes.soft : classes.strong}>{key ? t(key) : offer.status}</span>;
}
